import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import emailjs from '@emailjs/browser';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
// ============================================
// EMAILJS SETUP INSTRUCTIONS:
// ============================================
// 1. Go to https://www.emailjs.com/ and create a free account
// 2. Add an email service (Gmail, Outlook, etc.)
// 3. Create an email template with these variables:
//    - {{from_name}} - Full Name
//    - {{from_email}} - Email Address
//    - {{phone}} - Phone Number
//    - {{filing_status}} - Filing Status
//    - {{employment_type}} - Employment Type
//    - {{industry}} - Industry
//    - {{notes}} - Notes/Special Circumstances
// 4. Get your credentials from EmailJS dashboard:
//    - Service ID (from Email Services)
//    - Template ID (from Email Templates)
//    - Public Key (from Account > API Keys)
// 5. Replace the values below with your actual credentials:
const EMAILJS_SERVICE_ID = 'service_53f9cfi';
const EMAILJS_TEMPLATE_ID = 'template_hkaja5b';
const EMAILJS_PUBLIC_KEY = '-cOs25YounkCulYK_';
export function IntakeForm() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    filingStatus: 'Single',
    employmentType: 'W-2',
    industry: '',
    notes: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      // Send email via EmailJS
      const templateParams = {
        from_name: formData.fullName,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        filing_status: formData.filingStatus,
        employment_type: formData.employmentType,
        industry: formData.industry || 'Not specified',
        notes: formData.notes || 'No additional notes',
        to_email: 'info@emberquestsolutions.com'
      };
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setStatus('success');
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus('error');
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return <section id="intake-form" className="py-24 bg-white relative overflow-hidden" data-id="element-73">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-neutral-50 skew-x-12 transform translate-x-20 -z-10" data-id="element-74" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
        <div className="max-w-3xl mx-auto" data-id="element-76">
          <div className="text-center mb-12" data-id="element-77">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4" data-id="element-78">
              Let's Get Started
            </h2>
            <p className="text-lg text-neutral-600" data-id="element-79">
              Fill out this simple form to begin your stress-free tax
              experience.
            </p>
          </div>

          <AnimatePresence mode="wait" data-id="element-80">
            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-81">
                <Card className="p-8 border-2 border-secondary/30 bg-secondary/5" data-id="element-82">
                  <div className="text-center mb-8" data-id="element-83">
                    <div className="w-16 h-16 bg-secondary text-white rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-84">
                      <CheckCircle className="w-8 h-8" data-id="element-85" />
                    </div>
                    <h3 className="text-2xl font-bold text-primary-dark" data-id="element-86">
                      Request Received!
                    </h3>
                    <p className="text-neutral-600 mt-2" data-id="element-87">
                      Check your email for confirmation.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border border-neutral-200 p-6 shadow-sm" data-id="element-88">
                    <div className="border-b border-neutral-100 pb-4 mb-4" data-id="element-89">
                      <p className="text-sm text-neutral-500" data-id="element-90">Subject:</p>
                      <p className="font-medium text-neutral-800" data-id="element-91">
                        Thanks for Starting Your Tax Prep — We've Got You
                        Covered 🌴
                      </p>
                    </div>
                    <div className="prose prose-sm text-neutral-600 max-w-none" data-id="element-92">
                      <p data-id="element-93">Hi {formData.fullName.split(' ')[0]},</p>
                      <p data-id="element-94">
                        Thanks for submitting your tax prep intake form — your
                        information was received successfully. I'll review
                        everything and reach out shortly with next steps and any
                        documents needed to begin preparing your return.
                      </p>
                      <p data-id="element-95">
                        At Coastal Ember, we're committed to making tax season
                        feel calm, clear, and stress-free — like a breath of
                        ocean air. 🌊 Your return will be prepared by a licensed
                        CPA or Enrolled Agent (EA) to ensure accuracy,
                        compliance, and maximized deductions.
                      </p>
                      <p className="font-medium text-primary-dark" data-id="element-96">
                        What Happens Next:
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-97">
                        <li data-id="element-98">
                          I'll personally review your intake within 24 hours
                        </li>
                        <li data-id="element-99">
                          You'll receive a follow-up email with required
                          documents and timeline
                        </li>
                        <li data-id="element-100">
                          You'll receive a payment link (starting at $250)
                        </li>
                      </ul>
                      <p data-id="element-101">
                        Talk soon,
                        <br data-id="element-102" />
                        Neil Pereira
                        <br data-id="element-103" />
                        Tax Preparer, Coastal Ember
                        <br data-id="element-104" />
                        info@emberquestsolutions.com
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 text-center" data-id="element-105">
                    <Button onClick={() => setStatus('idle')} variant="outline" data-id="element-106">
                      Submit Another Response
                    </Button>
                  </div>
                </Card>
              </motion.div> : status === 'error' ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-107">
                <Card className="p-8 border-2 border-red-200 bg-red-50" data-id="element-108">
                  <div className="text-center" data-id="element-109">
                    <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-110">
                      <AlertCircle className="w-8 h-8" data-id="element-111" />
                    </div>
                    <h3 className="text-2xl font-bold text-red-900 mb-2" data-id="element-112">
                      Submission Failed
                    </h3>
                    <p className="text-red-700 mb-6" data-id="element-113">
                      We couldn't send your form. Please email us directly at
                      info@emberquestsolutions.com or try again.
                    </p>
                    <Button onClick={() => setStatus('idle')} variant="primary" data-id="element-114">
                      Try Again
                    </Button>
                  </div>
                </Card>
              </motion.div> : <motion.form initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-2xl shadow-lg border border-neutral-100" data-id="element-115">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-116">
                  <div className="space-y-2" data-id="element-117">
                    <label htmlFor="fullName" className="text-sm font-medium text-neutral-700" data-id="element-118">
                      Full Name *
                    </label>
                    <input required type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="Jane Doe" data-id="element-119" />
                  </div>

                  <div className="space-y-2" data-id="element-120">
                    <label htmlFor="email" className="text-sm font-medium text-neutral-700" data-id="element-121">
                      Email Address *
                    </label>
                    <input required type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="jane@example.com" data-id="element-122" />
                  </div>

                  <div className="space-y-2" data-id="element-123">
                    <label htmlFor="phone" className="text-sm font-medium text-neutral-700" data-id="element-124">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="(555) 123-4567" data-id="element-125" />
                  </div>

                  <div className="space-y-2" data-id="element-126">
                    <label htmlFor="filingStatus" className="text-sm font-medium text-neutral-700" data-id="element-127">
                      Filing Status
                    </label>
                    <select id="filingStatus" name="filingStatus" value={formData.filingStatus} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white" data-id="element-128">
                      <option data-id="element-129">Single</option>
                      <option data-id="element-130">Married Filing Jointly</option>
                      <option data-id="element-131">Married Filing Separately</option>
                      <option data-id="element-132">Head of Household</option>
                    </select>
                  </div>

                  <div className="space-y-2" data-id="element-133">
                    <label htmlFor="employmentType" className="text-sm font-medium text-neutral-700" data-id="element-134">
                      Employment Type
                    </label>
                    <select id="employmentType" name="employmentType" value={formData.employmentType} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white" data-id="element-135">
                      <option data-id="element-136">W-2 Employee</option>
                      <option data-id="element-137">1099 Contractor / Freelance</option>
                      <option data-id="element-138">Mixed (W-2 + 1099)</option>
                      <option data-id="element-139">Business Owner</option>
                    </select>
                  </div>

                  <div className="space-y-2" data-id="element-140">
                    <label htmlFor="industry" className="text-sm font-medium text-neutral-700" data-id="element-141">
                      Industry (Optional)
                    </label>
                    <select id="industry" name="industry" value={formData.industry} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white" data-id="element-142">
                      <option value="" data-id="element-143">Select an industry...</option>
                      <option data-id="element-144">Medical / Healthcare</option>
                      <option data-id="element-145">Real Estate</option>
                      <option data-id="element-146">Education</option>
                      <option data-id="element-147">Tech / Software</option>
                      <option data-id="element-148">Entertainment / Creative</option>
                      <option data-id="element-149">Other</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2" data-id="element-150">
                  <label htmlFor="notes" className="text-sm font-medium text-neutral-700" data-id="element-151">
                    Notes / Special Circumstances
                  </label>
                  <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={4} className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none" placeholder="Any specific questions or details we should know about?" data-id="element-152" />
                </div>

                <div className="pt-4" data-id="element-153">
                  <Button type="submit" className="w-full text-lg" disabled={status === 'submitting'} data-id="element-154">
                    {status === 'submitting' ? <span className="flex items-center justify-center" data-id="element-155">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-156">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-157"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-158"></path>
                        </svg>
                        Sending...
                      </span> : <span className="flex items-center justify-center" data-id="element-159">
                        Submit Intake <Send className="ml-2 w-5 h-5" data-id="element-160" />
                      </span>}
                  </Button>
                  <p className="text-center text-xs text-neutral-400 mt-4" data-id="element-161">
                    By submitting, you agree to be contacted by Coastal Ember.
                    Your data is secure.
                  </p>
                </div>
              </motion.form>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}